// Сценарий приглашения 2026-07-04 — публичная страница сброса пароля.
// URL: /reset/{token}. Пользователь по одноразовой ссылке (её выдал админ или
// заказчик проекта) видит свой email и задаёт новый пароль.
// Бэкенд: GET /password-resets/{token}, POST /password-resets/{token}/accept.
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { KeyRound, AlertTriangle } from 'lucide-react';
import { api } from '@/api/client';
import { Button } from '@/components/Button';

interface ResetInfo {
  email: string;
}

export default function ResetPassword() {
  const { token = '' } = useParams<{ token: string }>();
  const nav = useNavigate();

  const infoQ = useQuery<ResetInfo>({
    queryKey: ['password-reset', token],
    queryFn: () => api.get<ResetInfo>(`/password-resets/${token}`),
    retry: false,
  });

  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const accept = useMutation({
    mutationFn: () =>
      api.post<{ reset: boolean }>(`/password-resets/${token}/accept`, { password }),
    onSuccess: () => nav('/login?reset=1', { replace: true }),
    onError: (e: unknown) => setError((e as Error).message),
  });

  useEffect(() => { setError(''); }, [password]);

  if (infoQ.isLoading) {
    return <Centered>Проверяем ссылку…</Centered>;
  }
  if (infoQ.isError) {
    return (
      <Centered>
        <AlertTriangle size={32} color="#DC2626" style={{ margin: '0 auto 12px' }} />
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#DC2626' }}>Ссылка недействительна</h2>
        <p style={{ fontSize: 14, color: '#64748B', marginTop: 8 }}>
          Возможно, ссылка сброса устарела или уже использована. Запросите новую
          у администратора или заказчика проекта.
        </p>
      </Centered>
    );
  }
  const info = infoQ.data!;

  return (
    <Centered>
      <KeyRound size={32} color="#0A1628" style={{ margin: '0 auto 12px' }} />
      <h2 style={{ fontSize: 20, fontWeight: 600 }}>Новый пароль</h2>
      <p style={{ fontSize: 14, color: '#475569', marginTop: 8 }}>
        Задайте новый пароль для входа в систему Коллегиум.
      </p>
      <div style={{ marginTop: 24, textAlign: 'left' }}>
        <Field label="Email">
          <input value={info.email} disabled style={inputStyle} />
        </Field>
        <Field label="Новый пароль (минимум 8 символов)">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
          />
        </Field>
        {error && (
          <div style={{ padding: 8, background: '#FEE2E2', borderRadius: 6, color: '#991B1B', fontSize: 13, marginTop: 8 }}>
            {error}
          </div>
        )}
        <div style={{ marginTop: 16 }}>
          <Button
            variant="primary"
            onClick={() => accept.mutate()}
            disabled={password.length < 8 || accept.isPending}
          >
            {accept.isPending ? 'Сохраняем…' : 'Задать пароль и войти'}
          </Button>
        </div>
      </div>
    </Centered>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4, fontWeight: 500 }}>{label}</div>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #CBD5E1',
  borderRadius: 6,
  fontSize: 14,
};

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ maxWidth: 480, width: '100%', background: 'white', padding: 32, borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', textAlign: 'center' }}>
        {children}
      </div>
    </div>
  );
}
